import { CompassIcon, SquareLock02Icon } from '@hugeicons/react'
import clsx from 'clsx'

interface LevelThumbProps {
  url: string | null
  size: number
  locked?: boolean
  alt?: string
}

/**
 * Thumbnail de um nível de jornada. Exibe a imagem cadastrada (`level.icon`)
 * quando existir; caso contrário, um fallback consistente do mesmo tamanho:
 * box lilás com bússola (desbloqueado) ou box neutro com cadeado (bloqueado).
 * Usado na timeline/grid de níveis e na tela de início do nível.
 */
export function LevelThumb({ url, size, locked = false, alt = '' }: LevelThumbProps) {
  const glyphSize = Math.round(size * 0.5)

  // Sem imagem: box de fallback sempre com o mesmo tamanho/prominência.
  // Bloqueado vira um box neutro com cadeado (em vez de "lavar" o lilás e
  // parecer um ícone menor); desbloqueado mantém o lilás com a bússola.
  if (!url) {
    return (
      <div
        style={{ width: size, height: size }}
        className={clsx(
          'flex shrink-0 items-center justify-center rounded-lg',
          locked ? 'bg-[#ececf2]' : 'bg-brand-surface'
        )}
      >
        {locked ? (
          <SquareLock02Icon size={glyphSize} color="var(--color-gray-700)" strokeWidth={2} />
        ) : (
          <CompassIcon
            size={glyphSize}
            variant="duotone"
            className="text-primary opacity-45"
          />
        )}
      </div>
    )
  }

  return (
    <div className="relative shrink-0">
      <img
        src={url}
        alt={alt}
        width={size}
        height={size}
        style={{ width: size, height: size }}
        className={clsx(
          'rounded-lg object-cover block',
          locked && 'grayscale opacity-65'
        )}
      />
      {locked && (
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Chip escuro + cadeado branco: o chip branco com ícone cinza sumia
              sobre thumb esmaecida — o cadeado precisa ser o elemento mais
              visível do estado bloqueado, não um detalhe. */}
          <div className="rounded-full bg-gray-800/80 p-1.5 flex items-center justify-center">
            <SquareLock02Icon
              size={size >= 46 ? 16 : 14}
              color="#fff"
              strokeWidth={2}
            />
          </div>
        </div>
      )}
    </div>
  )
}
